// AskTab — RAG chat surface for the team knowledge library. Rendered as
// the "Ask" tab inside IntelligencePage; never appears standalone now that
// the IA has been collapsed (old /ask URL redirects into /knowledge?tab=ask).
//
// You ask a question; the Worker runs the query through Workers AI
// (embedding) → Vectorize (retrieval across documents AND commitments) →
// Claude Sonnet 4.5 with the matched chunks, any image hits as vision
// blocks, and a fresh live-data snapshot pulled from the desktop side. The
// answer streams back as SSE; citations arrive first as a separate event so
// we can paint source chips before the prose starts.

import { useEffect, useRef, useState } from "react";

import { useAuth } from "../../components/AuthGate";
import {
  askStream,
  type AskStage,
  type AskTurn,
  type Citation,
} from "../../lib/ask";
import {
  clearAskThread,
  loadAskThread,
  saveAskThread,
  type PersistedMessage,
} from "../../lib/ask-storage";
import { buildLiveSnapshot } from "../../lib/live-snapshot";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  /** Set on assistant turns once citations have streamed in. */
  citations?: Citation[];
  /** Latest pipeline stage update from the Worker. Cleared when the model
   * begins emitting content tokens — at that point the answer takes over
   * the visual real estate. */
  stage?: AskStage;
  /** True while this assistant turn is still receiving tokens. */
  streaming?: boolean;
  /** Set if the turn errored — replaces `content` in the UI. */
  error?: string;
}

/** Render the worker-reported stage as a short, scannable status line. The
 * goal isn't to explain the system; it's to make the wait feel like work in
 * progress rather than a magic-box pause. */
function stageLabel(stage: AskStage): string {
  switch (stage.phase) {
    case "embedding":
      return "Embedding your question…";
    case "retrieving":
      return "Searching the team library…";
    case "retrieved": {
      const docs =
        stage.docCount > 0
          ? `${stage.docHits} excerpt${stage.docHits === 1 ? "" : "s"} across ${stage.docCount} doc${stage.docCount === 1 ? "" : "s"}`
          : null;
      const commits =
        stage.commitmentHits > 0
          ? `${stage.commitmentHits} commitment${stage.commitmentHits === 1 ? "" : "s"}`
          : null;
      const parts = [docs, commits].filter((x): x is string => Boolean(x));
      if (parts.length === 0) return "No matches in the library — drafting anyway…";
      return `Found ${parts.join(" and ")}`;
    }
    case "loading_images":
      return `Reading ${stage.count} image${stage.count === 1 ? "" : "s"}…`;
    case "drafting":
      return "Drafting answer…";
  }
}

/**
 * @param hideHeader  When rendered as a tab inside IntelligencePage, the
 * parent owns the page header. Set hideHeader to suppress the duplicate
 * title bar and let the parent's tab nav take that real estate. Default
 * true here because that's the only context AskTab is rendered in now.
 */
export function AskTab({ hideHeader = true }: { hideHeader?: boolean } = {}) {
  const { session } = useAuth();
  const userEmail = session?.email ?? null;

  // Seed messages from localStorage so the thread survives tab switches
  // (Ask → Live data → Ask) AND app restarts. Keyed by user email so
  // signing in as someone else doesn't show their history. Lazy init
  // (passing a function) so we only touch localStorage once per mount.
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const persisted = loadAskThread(userEmail);
    return persisted.map((m) => ({
      role: m.role,
      content: m.content,
      citations: m.citations,
      error: m.error,
      // streaming / stage are intentionally NOT restored — they're
      // ephemeral per-turn UI state.
    }));
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const ctrlRef = useRef<AbortController | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll the conversation to the bottom whenever messages grow or
  // the in-flight assistant turn streams new tokens.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Persist on every state change. We strip ephemeral fields (stage,
  // streaming) and drop in-flight or empty placeholders before writing —
  // a half-completed "Thinking…" rehydrating after a tab switch would
  // confuse rather than help.
  useEffect(() => {
    const persistable: PersistedMessage[] = messages
      .filter((m) => !m.streaming && (m.content || m.error))
      .map((m) => ({
        role: m.role,
        content: m.content,
        citations: m.citations,
        error: m.error,
      }));
    saveAskThread(userEmail, persistable);
  }, [messages, userEmail]);

  // Cancel any in-flight stream when the user navigates away.
  useEffect(() => () => ctrlRef.current?.abort(), []);

  async function send() {
    const query = input.trim();
    if (!query || busy) return;
    setInput("");
    setBusy(true);

    // Snapshot the conversation we hand to the Worker — only completed
    // turns, never the placeholder we're about to add.
    const history: AskTurn[] = messages
      .filter((m) => !m.streaming && !m.error)
      .map((m) => ({ role: m.role, content: m.content }));

    // Append the user's turn + a streaming-assistant placeholder.
    setMessages((prev) => [
      ...prev,
      { role: "user", content: query },
      { role: "assistant", content: "", streaming: true },
    ]);

    // Pull a fresh live-data snapshot (Cut 4.2) — Metabase auth lives on
    // the desktop side, so we format the snapshot here and ship it with
    // the query. Best-effort; if it fails or times out we proceed with an
    // empty string and the model still has docs + commitments.
    let liveData = "";
    try {
      liveData = await buildLiveSnapshot();
    } catch (e) {
      console.warn("[ask] live snapshot failed", e);
    }

    let buffered = "";
    let receivedAnyDelta = false;
    ctrlRef.current = askStream(query, history, liveData, {
      onStage: (stage) => {
        setMessages((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            const m = next[i];
            if (m && m.role === "assistant" && m.streaming) {
              next[i] = { ...m, stage };
              break;
            }
          }
          return next;
        });
      },
      onCitations: (citations) => {
        setMessages((prev) => {
          // Patch citations onto the last assistant message.
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            const m = next[i];
            if (m && m.role === "assistant" && m.streaming) {
              next[i] = { ...m, citations };
              break;
            }
          }
          return next;
        });
      },
      onDelta: (delta) => {
        buffered += delta;
        receivedAnyDelta = true;
        setMessages((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            const m = next[i];
            if (m && m.role === "assistant" && m.streaming) {
              // First content token arrived — clear the stage status so the
              // answer can take over the visual real estate.
              next[i] = { ...m, content: buffered, stage: undefined };
              break;
            }
          }
          return next;
        });
      },
      onDone: () => {
        setMessages((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            const m = next[i];
            if (m && m.role === "assistant" && m.streaming) {
              next[i] = receivedAnyDelta
                ? { ...m, streaming: false }
                : {
                    ...m,
                    streaming: false,
                    error:
                      "The model produced no output. The team library may be empty, or the question didn't match any indexed content.",
                  };
              break;
            }
          }
          return next;
        });
        setBusy(false);
        ctrlRef.current = null;
      },
      onError: (msg) => {
        setMessages((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            const m = next[i];
            if (m && m.role === "assistant" && m.streaming) {
              next[i] = { ...m, streaming: false, error: msg };
              break;
            }
          }
          return next;
        });
        setBusy(false);
        ctrlRef.current = null;
      },
    });
  }

  function stop() {
    ctrlRef.current?.abort();
    ctrlRef.current = null;
    setMessages((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i >= 0; i--) {
        const m = next[i];
        if (m && m.role === "assistant" && m.streaming) {
          next[i] = m.content
            ? { ...m, streaming: false }
            : { ...m, streaming: false, error: "Stopped." };
          break;
        }
      }
      return next;
    });
    setBusy(false);
  }

  function clearThread() {
    if (busy) return;
    setMessages([]);
    // Belt-and-braces: the save-on-change effect would also clear the
    // entry (empty array → removeItem), but doing it explicitly here
    // makes the intent obvious and is a no-op if storage is already empty.
    clearAskThread(userEmail);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Cmd/Ctrl+Enter sends. Plain Enter inserts a newline (so multi-line
    // questions are easy to type).
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void send();
    }
  }

  return (
    <>
      {!hideHeader && (
        <div className="page-head">
          <div>
            <div className="page-head__meta">KNOWLEDGE · ASK</div>
            <h1>Ask the library</h1>
          </div>
          {messages.length > 0 && (
            <button
              type="button"
              onClick={clearThread}
              disabled={busy}
              style={clearBtn}
              title="Start a new conversation"
            >
              New conversation
            </button>
          )}
        </div>
      )}

      {hideHeader && messages.length > 0 && (
        <div style={surfaceToolbar}>
          <button
            type="button"
            onClick={clearThread}
            disabled={busy}
            style={clearBtn}
            title="Start a new conversation"
          >
            New conversation
          </button>
        </div>
      )}

      <p style={lede}>
        Every document the team has dropped into Knowledge is indexed — text
        chunks via Workers AI embeddings, images via Claude vision. Ask a
        question and the system retrieves the most relevant excerpts (top 8),
        cites them inline, and answers with Claude Sonnet 4.5. If the library
        doesn't have the answer, it'll say so.
      </p>

      <div ref={scrollerRef} style={scroller}>
        {messages.length === 0 && (
          <div style={emptyState}>
            <div style={emptyMono}>NO QUESTIONS YET</div>
            <div style={emptyHint}>
              Try: <em>"What's the life-occasion bundling thesis?"</em> or
              {" "}<em>"Who owns redemption right now and what are the blockers?"</em>
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <MessageView key={i} message={m} />
        ))}
      </div>

      <div style={composer}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask a question across every document in the team library…"
          rows={3}
          style={textarea}
          disabled={busy}
        />
        <div style={composerActions}>
          <span style={composerHint}>⌘↵ to send</span>
          {busy ? (
            <button type="button" onClick={stop} style={stopBtn}>
              Stop
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void send()}
              disabled={!input.trim()}
              style={sendBtn(Boolean(input.trim()))}
            >
              Ask
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// -----------------------------------------------------------------------

function MessageView({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";
  return (
    <div style={msgRow}>
      <div style={msgGutter(isUser)}>{isUser ? "YOU" : "ANALYST"}</div>
      <div style={msgBody}>
        {message.error ? (
          <div style={msgError}>{message.error}</div>
        ) : isUser ? (
          <div style={msgUserText}>{message.content}</div>
        ) : (
          <>
            {/* Pipeline status — only while we have a stage update and no
                content yet. First content token clears the stage in the
                state update, so this naturally disappears the moment the
                answer starts streaming. */}
            {message.streaming && message.stage && !message.content && (
              <div style={stageLine}>
                <span style={stagePulse} />
                <span style={stageText}>{stageLabel(message.stage)}</span>
              </div>
            )}
            <div style={msgAssistantText}>
              {message.content ||
                (message.streaming && !message.stage ? "Thinking…" : "")}
              {message.streaming && message.content && <span style={cursor}>▍</span>}
            </div>
          </>
        )}

        {!isUser && message.citations && message.citations.length > 0 && (
          <div style={citationsWrap}>
            <div style={citationsHead}>SOURCES</div>
            <div style={citationsList}>
              {message.citations.map((c, i) => {
                const isCommitment = c.source === "commitment";
                return (
                  <div key={i} style={citationCard(isCommitment)} title={c.snippet}>
                    <div style={citationTop}>
                      <span style={citationIdx(isCommitment)}>
                        [{isCommitment ? "C" : "D"}{i + 1}]
                      </span>
                      <span style={citationFile}>{c.fileName}</span>
                      <span style={citationScore}>{(c.score * 100).toFixed(0)}%</span>
                    </div>
                    <div style={citationSection}>{c.sectionLabel}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------- styles ------------------------------------------------

const lede: React.CSSProperties = {
  maxWidth: 760,
  fontSize: 13,
  color: "var(--ink-dim)",
  lineHeight: 1.6,
  marginBottom: 18,
};
const surfaceToolbar: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: 12,
};

const scroller: React.CSSProperties = {
  border: "1px solid var(--edge)",
  borderRadius: 6,
  padding: "16px 18px",
  minHeight: 360,
  maxHeight: "calc(100vh - 380px)",
  overflowY: "auto",
  background: "var(--panel-2)",
  marginBottom: 14,
};

const emptyState: React.CSSProperties = {
  padding: "40px 20px",
  textAlign: "center",
  color: "var(--ink-faint)",
  display: "flex",
  flexDirection: "column",
  gap: 10,
  alignItems: "center",
};
const emptyMono: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.18em",
  fontWeight: 700,
  color: "var(--purple)",
};
const emptyHint: React.CSSProperties = {
  fontSize: 12.5,
  lineHeight: 1.6,
  maxWidth: 440,
};

const msgRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "70px 1fr",
  gap: 14,
  padding: "14px 0",
  borderBottom: "0.5px solid var(--edge)",
};
function msgGutter(isUser: boolean): React.CSSProperties {
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 9,
    letterSpacing: "0.14em",
    fontWeight: 700,
    color: isUser ? "var(--ink-faint)" : "var(--purple)",
    paddingTop: 3,
  };
}
const msgBody: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  minWidth: 0,
};
const msgUserText: React.CSSProperties = {
  fontSize: 13.5,
  color: "var(--ink)",
  lineHeight: 1.55,
  whiteSpace: "pre-wrap",
};
const msgAssistantText: React.CSSProperties = {
  fontSize: 13.5,
  color: "var(--ink)",
  lineHeight: 1.65,
  whiteSpace: "pre-wrap",
};
const stageLine: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 0",
  marginBottom: 2,
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  letterSpacing: "0.02em",
  color: "var(--ink-faint)",
};
const stagePulse: React.CSSProperties = {
  display: "inline-block",
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: "var(--purple)",
  // CSS-keyframe pulse so the dot reads as "alive" — defined globally
  // in index.css under the `pulse` keyframe name.
  animation: "bvndle-pulse 1.1s ease-in-out infinite",
};
const stageText: React.CSSProperties = {
  // Subtle italic helps the status line read as ephemeral / system speech,
  // distinct from the analyst's own prose that follows.
  fontStyle: "italic",
};
const msgError: React.CSSProperties = {
  fontSize: 12.5,
  color: "var(--coral)",
  background: "rgba(196, 74, 74, 0.08)",
  borderLeft: "3px solid var(--coral)",
  padding: "8px 12px",
  borderRadius: 3,
  whiteSpace: "pre-wrap",
};
const cursor: React.CSSProperties = {
  display: "inline-block",
  marginLeft: 2,
  color: "var(--purple)",
  animation: "none",
};

const citationsWrap: React.CSSProperties = {
  marginTop: 4,
  paddingTop: 10,
  borderTop: "0.5px dashed var(--edge)",
};
const citationsHead: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9,
  letterSpacing: "0.16em",
  fontWeight: 700,
  color: "var(--ink-faint)",
  marginBottom: 8,
};
const citationsList: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
  gap: 8,
};
function citationCard(isCommitment: boolean): React.CSSProperties {
  return {
    padding: "8px 10px",
    border: `1px solid ${isCommitment ? "rgba(94, 201, 140, 0.45)" : "var(--edge)"}`,
    borderLeft: `3px solid ${isCommitment ? "#5ec98c" : "var(--purple)"}`,
    borderRadius: 4,
    background: isCommitment ? "rgba(94, 201, 140, 0.07)" : "rgba(0,0,0,0.18)",
    cursor: "help",
  };
}
const citationTop: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginBottom: 3,
};
function citationIdx(isCommitment: boolean): React.CSSProperties {
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 9.5,
    fontWeight: 700,
    color: isCommitment ? "#5ec98c" : "var(--purple)",
  };
}
const citationFile: React.CSSProperties = {
  fontSize: 11.5,
  fontWeight: 600,
  color: "var(--ink)",
  flex: 1,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const citationScore: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9,
  color: "var(--ink-faint)",
};
const citationSection: React.CSSProperties = {
  fontSize: 10.5,
  color: "var(--ink-dim)",
  fontFamily: "var(--font-mono)",
  letterSpacing: "0.04em",
};

const composer: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  border: "1px solid var(--edge)",
  borderRadius: 6,
  padding: 12,
  background: "var(--panel-2)",
};
const textarea: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--ink)",
  fontSize: 13.5,
  fontFamily: "inherit",
  lineHeight: 1.5,
  resize: "vertical",
  outline: "none",
  padding: 4,
  minHeight: 60,
};
const composerActions: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
};
const composerHint: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9.5,
  letterSpacing: "0.12em",
  color: "var(--ink-faint)",
  textTransform: "uppercase",
};
function sendBtn(active: boolean): React.CSSProperties {
  return {
    padding: "7px 16px",
    border: `1px solid ${active ? "var(--purple)" : "var(--edge)"}`,
    background: active ? "var(--purple)" : "transparent",
    color: active ? "#fff" : "var(--ink-faint)",
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    letterSpacing: "0.14em",
    fontWeight: 700,
    textTransform: "uppercase",
    borderRadius: 4,
    cursor: active ? "pointer" : "not-allowed",
    transition: "all 120ms ease",
  };
}
const stopBtn: React.CSSProperties = {
  padding: "7px 16px",
  border: "1px solid var(--coral)",
  background: "transparent",
  color: "var(--coral)",
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.14em",
  fontWeight: 700,
  textTransform: "uppercase",
  borderRadius: 4,
  cursor: "pointer",
};
const clearBtn: React.CSSProperties = {
  padding: "6px 14px",
  border: "1px solid var(--edge)",
  background: "transparent",
  color: "var(--ink-faint)",
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.14em",
  fontWeight: 700,
  textTransform: "uppercase",
  borderRadius: 4,
  cursor: "pointer",
};
